import { api, RequestOptions } from '@/api/api';
import { Tag, TagForm, TagRecommendation, TrackTag } from '@/types';
import { Route } from 'vue-router';
import { OrderDir } from '@/api/index';

export enum TagOrderColumn {
  id = 'id',
  name = 'name',
  trackCount = 'trackCount'
}

export interface TagServiceListParams {
  start?: number;
  limit?: number;
  order_column?: TagOrderColumn;
  order_dir?: OrderDir;
  search?: string;
  ids?: number[];
  exclude_ids?: number[];
}

export interface TagServiceList {
  total: number;
  list: Tag[];
}

export interface TagServiceRecommendParams {
  track_ids: number[];
}

export const TagService = {
  prepareListParams(r: Route, defaultLimit: number): TagServiceListParams {
    const res: TagServiceListParams = {
      limit: defaultLimit,
      start: 0
    };

    if (r.query.limit) {
      res.limit = parseInt(r.query.limit.toString(), 10);
    }

    if (r.query.start) {
      res.start = parseInt(r.query.start.toString(), 10);
    }

    switch (r.query.sortField) {
      case TagOrderColumn.id:
      case TagOrderColumn.name:
      case TagOrderColumn.trackCount:
        res.order_column = r.query.sortField;
    }

    switch (r.query.sortOrder) {
      case OrderDir.asc:
      case OrderDir.desc:
        res.order_dir = r.query.sortOrder;
        break;
    }

    if (r.query.search) {
      res.search = r.query.search.toString();
    }

    if (r.query.ids) {
      res.ids = r.query.ids
        .toString()
        .split(',')
        .map((id) => parseInt(id, 10));
    }

    if (r.query.exclude_ids) {
      res.exclude_ids = r.query.exclude_ids
        .toString()
        .split(',')
        .map((id) => parseInt(id, 10));
    }

    return res;
  },

  list(params: TagServiceListParams, opts?: RequestOptions): Promise<TagServiceList> {
    // eslint-disable-next-line
    const query: Record<string, any> = { ...params };

    if (params.ids) {
      query['ids'] = params.ids.join(',');
    }

    if (params.exclude_ids) {
      query['exclude_ids'] = params.exclude_ids.join(',');
    }

    return api.get('/tag/list', query, opts);
  },

  // Рекомендация тегов по CLAP-векторам выбранных треков. Сначала жанр/инструмент
  // (до limit ближайших), затем один vocal-тег. Теги без вектора у треков игнорятся.
  recommend(params: TagServiceRecommendParams, opts?: RequestOptions): Promise<TagRecommendation[]> {
    return api.post('/tag/recommend', params, opts);
  },

  syncTrack(track_id: number, tags: { name: string }[]): Promise<TrackTag[]> {
    return api.post('/tag/sync_track', {
      track_id,
      tags
    });
  },

  attachTracks(track_ids: number[], tags: { name: string }[]): Promise<void> {
    return api.post('/tag/attach_tracks', {
      track_ids,
      tags
    });
  },

  detachTracks(track_ids: number[], tag_ids: number[]): Promise<void> {
    return api.post('/tag/detach_tracks', {
      track_ids,
      tag_ids
    });
  },

  upsert(form: TagForm): Promise<Tag> {
    const formData = new FormData();

    if (form.id) {
      formData.append('id', form.id.toString());
    }

    form.search_aliases.forEach((alias) => {
      formData.append('search_aliases[]', alias);
    });

    formData.append('name', form.name);

    return api.post('/tag', formData);
  },

  delete(id: number, targetID?: number): Promise<void> {
    const qs = new URLSearchParams();
    qs.set('id', id.toString());

    if (targetID) {
      qs.set('target_id', targetID.toString());
    }

    return api.delete(`/tag?${qs}`);
  }
};
